import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IAdminProfession } from '@apis/adminProfessions';
import type { AdminUserRole, IAdminUser } from '@apis/adminUsers';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { Field } from '@components/ui/Field/Field';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { MultiSelect } from '@components/ui/MultiSelect/MultiSelect';
import { PhoneField } from '@components/ui/PhoneField/PhoneField';
import type { ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import { Pill } from '@components/ui/Pill/Pill';
import { professionGroups } from '../professionGroups';
import { useSsoDiscovery } from '../hooks/useSsoDiscovery';
import styles from './UserDialog.module.css';

export type UserLanguage = 'en' | 'ja';

export interface IUserDraft {
  fullName: string;
  email: string;
  phone: string;
  permission: AdminUserRole | null;
  /** Meaningful only when permission is VIEWER; empty otherwise. */
  permittedProfessionsIds: string[];
  language: UserLanguage | null;
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

// Role radio group: project-scoped roles only (backoffice roles excluded).
const PERMISSIONS: { value: AdminUserRole; labelKey: string; descriptionKey: string }[] = [
  {
    value: 'PROJECT_ADMIN',
    labelKey: 'adminUsers.permission.projectAdmin',
    descriptionKey: 'adminUsers.permissionDescription.projectAdmin',
  },
  {
    value: 'MANAGER',
    labelKey: 'adminUsers.permission.manager',
    descriptionKey: 'adminUsers.permissionDescription.manager',
  },
  {
    value: 'VIEWER',
    labelKey: 'adminUsers.permission.viewer',
    descriptionKey: 'adminUsers.permissionDescription.viewer',
  },
];

const ROLE_VALUES: readonly string[] = PERMISSIONS.map((p) => p.value);
const DEFAULT_LANGUAGE: UserLanguage = 'en';

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((id, i) => id === sortedB[i]);
}

interface IUserDialogProps {
  open: boolean;
  user: IAdminUser | null; // null → create mode; set → edit mode (email is locked).
  professions: IAdminProfession[];
  /**
   * Edit mode: the user's stored UI language — undefined while loading,
   * null when the user never saved one. Drives the prefill and the
   * "language changed" part of the edit-enable check.
   */
  initialLanguage?: UserLanguage | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (draft: IUserDraft) => void;
}

export function UserDialog({
  open,
  user,
  professions,
  initialLanguage,
  submitting,
  onClose,
  onSubmit,
}: IUserDialogProps) {
  const { t } = useTranslation();
  const isEdit = user !== null;

  const initialName = user?.name ?? user?.username ?? '';
  const initialPhone = user?.phone ?? '';
  const initialRole =
    user?.role && ROLE_VALUES.includes(user.role) ? (user.role as AdminUserRole) : null;
  const initialIds = useMemo(() => user?.permittedProfessionsIds ?? [], [user]);

  const [fullName, setFullName] = useState(initialName);
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(initialPhone);
  const [permission, setPermission] = useState<AdminUserRole | null>(initialRole);
  const [professionIds, setProfessionIds] = useState<string[]>(initialIds);
  // The admin's explicit pick; until then the stored preference (edit) or the
  // default (create) shows — so the prefill needs no state syncing.
  const [languageOverride, setLanguageOverride] = useState<UserLanguage | null>(null);
  const [phoneHasError, setPhoneHasError] = useState(false);

  const language = languageOverride ?? (isEdit ? (initialLanguage ?? null) : DEFAULT_LANGUAGE);

  // Read-only: the email domain decides how someone signs in, nobody picks it.
  // Edit mode already knows; create mode asks as the address is typed.
  const discovery = useSsoDiscovery(email, !isEdit);
  const ssoBadge = isEdit
    ? user.signInMethod === 'sso'
      ? t('adminUsers.field.ssoBadgeGeneric')
      : null
    : discovery?.sso
      ? discovery.displayName
        ? t('adminUsers.field.ssoBadge', { name: discovery.displayName })
        : t('adminUsers.field.ssoBadgeGeneric')
      : null;

  const languageOptions = useMemo<ISelectionOption[]>(
    () => [
      { id: 'en', label: t('adminUsers.language.en') },
      { id: 'ja', label: t('adminUsers.language.ja') },
    ],
    [t],
  );

  const groups = useMemo(() => professionGroups(t, professions), [t, professions]);
  const hasProfessions = groups.some((g) => g.options.length > 0);

  // ── Edit-enable: submit only when something actually changed ─────────
  const professionsValid = professionIds.length > 0 && !sameIds(professionIds, initialIds);
  const roleChangeValid =
    permission !== null &&
    permission !== initialRole &&
    (permission !== 'VIEWER' || professionsValid);
  const viewerProfessionsChanged = permission === 'VIEWER' && professionsValid;
  const languageChanged = language !== null && language !== (initialLanguage ?? null);

  const editEnabled =
    fullName.trim() !== '' &&
    !phoneHasError &&
    (fullName !== initialName ||
      phone !== initialPhone ||
      roleChangeValid ||
      viewerProfessionsChanged ||
      languageChanged);

  const createEnabled =
    fullName.trim() !== '' &&
    EMAIL_RE.test(email.trim()) &&
    permission !== null &&
    (permission !== 'VIEWER' || professionIds.length > 0) &&
    !phoneHasError;

  const canSubmit = (isEdit ? editEnabled : createEnabled) && !submitting;

  function handleClose() {
    setFullName(initialName);
    setEmail(user?.email ?? '');
    setPhone(initialPhone);
    setPermission(initialRole);
    setProfessionIds(initialIds);
    setLanguageOverride(null);
    setPhoneHasError(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? t('adminUsers.dialog.editTitle') : t('adminUsers.dialog.createTitle')}
      footer={
        <>
          <span className={styles.footerSpacer} />
          <Button variant="secondary" size="md" onClick={handleClose}>
            {t('adminUsers.dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                fullName: fullName.trim(),
                email: email.trim(),
                phone: phone.trim(),
                permission,
                permittedProfessionsIds: permission === 'VIEWER' ? professionIds : [],
                language,
              })
            }
          >
            {isEdit ? t('adminUsers.dialog.editSubmit') : t('adminUsers.dialog.createSubmit')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field
          label={t('adminUsers.field.fullName')}
          requiredMark
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder={t('adminUsers.field.fullNamePlaceholder')}
          required
          maxLength={200}
        />
        <Field
          label={t('adminUsers.field.email')}
          requiredMark
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={isEdit ? undefined : t('adminUsers.field.emailPlaceholder')}
          required
          disabled={isEdit}
          maxLength={320}
        />
        {ssoBadge ? (
          <div className={styles.ssoBadgeRow} role="status">
            <Pill className={styles.ssoBadge}>{ssoBadge}</Pill>
          </div>
        ) : null}
        <PhoneField
          label={t('adminUsers.field.phone')}
          value={phone}
          onChange={setPhone}
          invalidMessage={t('adminUsers.field.phoneInvalid')}
          onErrorChange={setPhoneHasError}
        />
        <div className={styles.formField}>
          <span className={styles.formLabel}>
            {t('adminUsers.field.permission')}
            <span className={styles.requiredMark} aria-hidden="true">
              *
            </span>
          </span>
          <div className={styles.checkGroup}>
            {PERMISSIONS.map((option) => (
              <div key={option.value} className={styles.permissionOption}>
                <button
                  type="button"
                  className={styles.checkRow}
                  aria-pressed={permission === option.value}
                  onClick={() => setPermission(option.value)}
                >
                  <Checkbox checked={permission === option.value} ariaLabel="" presentational />
                  <span className={styles.permissionText}>
                    <span>{t(option.labelKey)}</span>
                    <span className={styles.permissionDescription}>{t(option.descriptionKey)}</span>
                  </span>
                </button>
                {option.value === 'VIEWER' &&
                  permission === 'VIEWER' &&
                  (hasProfessions ? (
                    <div className={styles.professionsSelect}>
                      <MultiSelect
                        value={professionIds}
                        onChange={setProfessionIds}
                        groups={groups}
                        placeholder={t('adminUsers.field.professions')}
                        ariaLabel={t('adminUsers.field.professions')}
                        elevated
                      />
                    </div>
                  ) : (
                    <p className={styles.professionsWarning}>
                      {t('adminUsers.noProfessionsAvailable')}
                    </p>
                  ))}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.formField}>
          <span className={styles.formLabel}>{t('adminUsers.field.language')}</span>
          <SingleSelect
            value={language}
            onChange={(next) => setLanguageOverride(next as UserLanguage)}
            options={languageOptions}
            placeholder={t('adminUsers.field.language')}
            ariaLabel={t('adminUsers.field.language')}
            elevated
          />
        </div>
      </div>
    </Modal>
  );
}
